/**
 * @fileoverview Hook para acessar dados do contexto global via Redux.
 * Lê do slice globalData (single source of truth) — reativo a mudanças.
 *
 * @module useLocalData
 * @since 2.0.0
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectActiveProducts,
  selectCategories,
  selectBrands,
  selectPromotions,
} from '../store/slices/globalDataSlice';

/**
 * Hook para acessar dados globais reativos (products, categories, brands, promotions).
 * @returns {Object} Dados globais com funções de busca e status
 */
export function useLocalData() {
  const products = useSelector(selectActiveProducts);
  const categories = useSelector(selectCategories);
  const brands = useSelector(selectBrands);
  const promotions = useSelector(selectPromotions);
 
  /**
   * Filtra produtos por categoria.
   * @param {string} categoryName - Nome da categoria
   * @returns {Array} Produtos da categoria
   */
  const getProductsByCategory = useMemo(() => {
    return (categoryName) => {
      if (!categoryName) return [];
      // filter: mantém apenas produtos da categoria informada
      return products.filter(product =>
        product.categoria?.toLowerCase() === categoryName.toLowerCase()
      );
    };
  }, [products]);

  /**
   * Filtra produtos por marca.
   * @param {string} brandName - Nome da marca
   * @returns {Array} Produtos da marca
   */
  const getProductsByBrand = useMemo(() => {
    return (brandName) => {
      if (!brandName) return [];
      // filter: mantém apenas produtos da marca informada
      return products.filter(product =>
        product.marca?.toLowerCase() === brandName.toLowerCase()
      );
    };
  }, [products]);

  /**
   * Busca produtos por termo.
   * @param {string} searchTerm - Termo de busca
   * @returns {Array} Produtos encontrados
   */
  const searchProducts = useMemo(() => {
    return (searchTerm) => {
      if (!searchTerm || searchTerm.trim() === '') return [];
      const query = searchTerm.toLowerCase();
      // filter: busca em nome, descrição, marca e categoria
      return products.filter(product =>
        product.nome?.toLowerCase().includes(query) ||
        product.descricao?.toLowerCase().includes(query) ||
        product.marca?.toLowerCase().includes(query) ||
        product.categoria?.toLowerCase().includes(query)
      );
    };
  }, [products]);

  /**
   * Obtém produto por ID.
   * @param {number|string} productId - ID do produto
   * @returns {Object|null} Produto encontrado ou null
   */
  const getProductById = useMemo(() => {
    return (productId) => {
      if (!productId) return null;
      return products.find(product => product.id == productId) || null;
    };
  }, [products]);

  /**
   * Obtém categoria por nome.
   * @param {string} categoryName - Nome da categoria
   * @returns {Object|null} Categoria encontrada ou null
   */
  const getCategoryByName = useMemo(() => {
    return (categoryName) => {
      if (!categoryName) return null;
      return categories.find(category =>
        category.name?.toLowerCase() === categoryName.toLowerCase()
      ) || null;
    };
  }, [categories]);

  /**
   * Obtém marca por nome.
   * @param {string} brandName - Nome da marca
   * @returns {Object|null} Marca encontrada ou null
   */
  const getBrandByName = useMemo(() => {
    return (brandName) => {
      if (!brandName) return null;
      return brands.find(brand =>
        brand.name?.toLowerCase() === brandName.toLowerCase()
      ) || null;
    };
  }, [brands]);

  const hasData = useMemo(() => ({
    products: products.length > 0,
    categories: categories.length > 0,
    brands: brands.length > 0,
    promotions: promotions.length > 0,
    any: products.length > 0 || categories.length > 0 || brands.length > 0 || promotions.length > 0,
  }), [products.length, categories.length, brands.length, promotions.length]);

  const counts = useMemo(() => ({
    products: products.length,
    categories: categories.length,
    brands: brands.length,
    promotions: promotions.length,
  }), [products.length, categories.length, brands.length, promotions.length]);

  return {
    products,
    categories,
    brands,
    promotions,
    getProductsByCategory,
    getProductsByBrand,
    searchProducts,
    getProductById,
    getCategoryByName,
    getBrandByName,
    hasData,
    counts,
    isEmpty: !hasData.any,
    isLoaded: hasData.any,
  };
}

export default useLocalData;
